package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.BillingInterval
import dev.lingvo.shared.BillingPlanOfferDto
import dev.lingvo.shared.Plan
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LStatRow
import dev.lingvo.site.ds.LStatRowItem
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.ScreenshotPlanCapacity
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Respond to the available content width, including the app's settings navigation. */
internal fun planGridModifier(): Modifier = Modifier.fillMaxWidth().styleModifier {
    property("display", "grid")
    property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 280px), 1fr))")
    property("gap", Space.LG.toString())
    property("align-items", "stretch")
}

@Composable
internal fun PlanCard(
    plan: Plan,
    seats: Int,
    offer: BillingPlanOfferDto?,
    current: Boolean,
    canAct: Boolean,
    busy: Boolean,
    enabled: Boolean = true,
    onChoose: () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val credits = plan.creditsFor(seats)
    LCard(
        Modifier.fillMaxWidth()
            .then(if (current) Modifier.backgroundColor(palette.surfaceSunken) else Modifier)
            .styleModifier {
                property("display", "flex")
                property("min-width", "0")
            },
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                LSectionTitle(plan.displayName, Modifier.flexGrow(1).fontSize(TypeScale.HeadingSmall))
                when {
                    current -> LBadge(
                        t("app.settings.billing.plan_comparison.current_plan", "Current plan"),
                        variant = LBadgeVariant.SUCCESS,
                    )
                    plan == Plan.TEAM && offer?.available == false -> LBadge(
                        t("app.settings.billing.plan_comparison.on_request", "On request"),
                    )
                }
            }
            LMuted(planPurpose(plan))
            Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                if (offer != null) {
                    Span(Modifier.fontSize(TypeScale.Heading).fontWeight(600).tabularNumbers().toAttrs()) {
                        Text(if (plan == Plan.FREE) t("app.settings.billing.plan_comparison.free", "Free") else offer.pricePerSeat.display)
                    }
                    if (plan != Plan.FREE) {
                        LMuted(
                            if (offer.interval == BillingInterval.MONTH) {
                                t("app.settings.billing.plan_comparison.per_owner_or_admin_month", "per owner or admin / month")
                            } else {
                                t("app.settings.billing.plan_comparison.per_owner_or_admin_year", "per owner or admin / year")
                            },
                        )
                    }
                }
                if (plan != Plan.FREE || offer == null) {
                    LMuted(planOfferPriceLine(offer, seats))
                } else {
                    LMuted(t("app.settings.billing.plan_comparison.no_subscription_required", "No subscription required."))
                }
            }
            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                LMuted(t("app.settings.billing.plan_comparison.monthly_credit_capacity_at_your_current", "Monthly credit capacity at your current seat count"))
                // Static, common scale. The exact allowance and plan limits are in the table below.
                Div(
                    Modifier.fillMaxWidth().height(8.px).borderRadius(Radius.Pill)
                        .backgroundColor(palette.border)
                        .attr("aria-hidden", "true").toAttrs(),
                ) {
                    Div(
                        Modifier.height(8.px).borderRadius(Radius.Pill)
                            .width((credits * 100.0 / Plan.TEAM.creditsFor(seats)).percent)
                            .backgroundColor(palette.accent).toAttrs(),
                    )
                }
                LStatRow(
                    rows = listOf(
                        LStatRowItem(t("app.settings.billing.plan_comparison.monthly_workspace_credits", "Monthly workspace credits"), formatCount(credits)),
                        LStatRowItem(t("app.settings.billing.plan_comparison.projects", "Projects"), plan.maxProjects?.let(::formatCount) ?: t("app.settings.billing.plan_comparison.unlimited", "Unlimited")),
                        LStatRowItem(t("app.settings.billing.plan_comparison.owner_or_admin_seats", "Owner or admin seats"), plan.maxBillableSeats?.let(::formatCount) ?: t("app.settings.billing.plan_comparison.unlimited", "Unlimited")),
                    ),
                    caption = tf("app.settings.billing.plans.allowance_caption", "{plan} workspace allowance", "plan" to plan.displayName),
                )
            }
            ScreenshotPlanCapacity(plan)
            Column(Modifier.fillMaxWidth().gap(Space.SM).styleModifier { property("margin-top", "auto") }) {
                if (!current && offer?.available == false) {
                    offer.unavailableReason?.let { LMuted(it) }
                }
                if (canAct && offer?.available == true) {
                    LButton(
                        tf("app.settings.billing.plans.choose_plan", "Choose {plan}", "plan" to plan.displayName),
                        onChoose,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = enabled && !busy,
                        loading = busy,
                        variant = LButtonVariant.PRIMARY,
                    )
                } else if (!current && plan == Plan.TEAM && offer?.available == false) {
                    LLinkButton(
                        label = t("app.settings.billing.plan_comparison.ask_about_team", "Ask about Team"),
                        path = "mailto:support@lingvo.dev?subject=Lingvo%20Team%20plan",
                        variant = LButtonVariant.SECONDARY,
                    )
                }
            }
        }
    }
}

private fun planPurpose(plan: Plan): String = when (plan) {
    Plan.FREE -> tr("app.settings.billing.plan_comparison.a_first_project_with_your_own", "A first project, with your own provider keys or included credits.")
    Plan.STUDIO -> tr("app.settings.billing.plan_comparison.unlimited_projects_and_a_larger_monthly", "Unlimited projects and a larger monthly translation allowance.")
    Plan.TEAM -> tr("app.settings.billing.plan_comparison.more_included_credits_for_larger_translation", "More included credits for larger translation workloads.")
}

@Composable
internal fun CommonPlanInclusions() {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            LSectionTitle(t("app.settings.billing.plan_comparison.included_on_every_plan", "Included on every plan"), Modifier.fontSize(TypeScale.HeadingSmall))
            Div(planGridModifier().toAttrs()) {
                Column(Modifier.gap(Space.SM)) {
                    Span(Modifier.fontWeight(600).toAttrs()) { Text(t("app.settings.billing.plan_comparison.bring_your_own_provider_key", "Bring your own provider key")) }
                    LMuted(t("app.settings.billing.plan_comparison.use_your_own_configured_provider_without", "Use your own configured provider without spending Lingvo credits. Your provider may charge for its usage."))
                }
                Column(Modifier.gap(Space.SM)) {
                    Span(Modifier.fontWeight(600).toAttrs()) { Text(t("app.settings.billing.plan_comparison.translators_and_viewers_are_free", "Translators and viewers are free")) }
                    LMuted(t("app.settings.billing.plan_comparison.only_owners_and_admins_are_billable", "Only owners and admins are billable seats. Strings and target languages are unlimited."))
                }
                Column(Modifier.gap(Space.SM)) {
                    Span(Modifier.fontWeight(600).toAttrs()) { Text(t("app.settings.billing.plan_comparison.the_complete_localization_workflow", "The complete localization workflow")) }
                    LMuted(t("app.settings.billing.plan_comparison.import_and_export_manual_translation_review", "Import and export, manual translation, review, QA, CLI and API access stay available on Free."))
                }
                Column(Modifier.gap(Space.SM)) {
                    Span(Modifier.fontWeight(600).toAttrs()) { Text(t("app.settings.billing.plan_comparison.screenshots_every_plan", "Screenshots on every plan")) }
                    LMuted(t("app.settings.billing.plan_comparison.shared_screenshot_workflow", "Free includes both stores, the retake queue and screenshot push. Renders and pushes are not metered; storage uses the project and workspace byte limits."))
                }
            }
        }
    }
}
